import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';

import { CoiService, CoiData } from '../../../services/coi';
import { CoiDownloader } from '../../../services/coi-downloader';
import { ToastService } from '../../../services/toast';
import { IndDatePipe } from '../../../pipes/ind-date';
import { SendCoiDialog } from '../send-coi/send-coi-dialog';

/**
 * Presentational Certificate of Insurance — the dialog, the /policies/certificate/:memberId page,
 * and the finder share it. Loads coiData(memberId) for the summary header, then (when the policy is
 * issued) renders the SERVER HTML — the full Generali layout, logo + signature — in a sandboxed
 * iframe via [srcdoc]. Print / Download are enabled only once a policyNo exists.
 */
@Component({
  selector: 'app-coi-view',
  standalone: true,
  imports: [CommonModule, ButtonModule, TagModule, SkeletonModule, IndDatePipe, SendCoiDialog],
  templateUrl: './coi-view.html',
  styleUrls: ['./coi-view.css'],
})
export class CoiView {
  private svc = inject(CoiService);
  private downloader = inject(CoiDownloader);
  private toast = inject(ToastService);
  private sanitizer = inject(DomSanitizer);
  private platformId = inject(PLATFORM_ID);

  memberId = input<number | null>(null);

  data = signal<CoiData | null>(null);
  loading = signal(false);
  notFound = signal(false);
  /** The server certificate HTML, bypass-sanitized for iframe srcdoc (it carries <style>/SVG). */
  coiHtml = signal<SafeHtml | null>(null);
  htmlLoading = signal(false);
  printing = signal(false);
  /** Which download is in flight, so only the clicked button spins (not all three). */
  downloadKind = signal<'' | 'combined' | 'coi' | 'welcome'>('');
  readonly downloading = computed(() => this.downloadKind() !== '');
  /** "Send to customer" (queue-based WhatsApp / SMS / Email) dialog. */
  showSend = signal(false);

  /** Dedupe loads so the effect doesn't re-fetch the same member on unrelated re-runs. */
  private loadedKey = '';

  constructor() {
    effect(() => {
      const id = this.memberId();
      if (id && id > 0) this.load(id);
      else {
        this.loadedKey = '';
        this.notFound.set(false);
        this.data.set(null);
        this.coiHtml.set(null);
      }
    });
  }

  /** A certificate exists only once the policy is issued — i.e. a policyNo was stamped. */
  readonly isIssued = computed(() => !!this.data()?.policyNo);

  /** Welcome-letter downloads apply only to partner-onboarded members (not client members). */
  readonly canWelcome = computed(() => !this.data()?.clientName);

  private load(id: number) {
    if (!isPlatformBrowser(this.platformId)) return; // SSR: defer to the post-hydration run
    if (this.loadedKey === `m:${id}`) return;
    this.loadedKey = `m:${id}`;
    this.loading.set(true);
    this.notFound.set(false);
    this.coiHtml.set(null);
    this.svc.coiData(id).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (res?.success && res?.data) {
          const d = res.data as CoiData;
          this.data.set(d);
          if (d.policyNo) this.loadHtml(id); // issued → fetch the full certificate HTML
        } else {
          this.data.set(null);
          this.notFound.set(true);
          this.toast.error(res?.message || 'Certificate not found.');
        }
      },
      error: (e) => {
        this.loading.set(false);
        this.data.set(null);
        this.notFound.set(true);
        this.toast.error(e?.error?.message || 'Could not load the certificate.');
      },
    });
  }

  private loadHtml(id: number) {
    this.htmlLoading.set(true);
    this.downloader.loadCoiHtml(id).then((html) => {
      this.htmlLoading.set(false);
      // Only set if this is still the member we're showing (guard against a fast member switch).
      if (this.loadedKey === `m:${id}` && html) {
        this.coiHtml.set(this.sanitizer.bypassSecurityTrustHtml(html));
      }
    });
  }

  // ===================== actions =====================
  async print() {
    const id = this.data()?.memberId;
    if (!id || this.printing()) return;
    this.printing.set(true);
    try {
      await this.downloader.printCoi(id);
    } finally {
      this.printing.set(false);
    }
  }

  /** Combined welcome letter + COI (default). Falls back to COI alone for client members. */
  async download() {
    const d = this.data();
    if (!d || this.downloading()) return;
    this.downloadKind.set('combined');
    try {
      await this.downloader.downloadCoi(d.memberId, d.policyNo ?? undefined);
    } finally {
      this.downloadKind.set('');
    }
  }

  /** The certificate ALONE — no welcome letter prepended. */
  async downloadCoiOnly() {
    const d = this.data();
    if (!d || this.downloading()) return;
    this.downloadKind.set('coi');
    try {
      await this.downloader.downloadCoi(d.memberId, d.policyNo ?? undefined, true);
    } finally {
      this.downloadKind.set('');
    }
  }

  /** The welcome letter ONLY (partner-onboarded members). */
  async downloadWelcomeLetter() {
    const d = this.data();
    if (!d || this.downloading()) return;
    this.downloadKind.set('welcome');
    try {
      await this.downloader.downloadWelcome(d.memberId, d.policyNo ?? undefined);
    } finally {
      this.downloadKind.set('');
    }
  }
}
